import { computed, nextTick, onBeforeUnmount, ref, watch, type Ref } from 'vue'
import {
  clampDialogResizeHeight,
  clampDialogResizeWidth,
  maxResizableDialogHeight,
  maxResizableDialogWidth,
  PLATFORM_DIALOG_MIN_HEIGHT,
} from '@/common/utils/estimateFormContentWidth'
import { saveDialogSize } from '@/common/utils/platformDialogSizeStorage'

export const PLATFORM_DIALOG_RESIZING_CLASS = 'platform-dialog-resizing'

const SIZE_EQ_PX = 2

export interface UsePlatformDialogResizeOptions {
  dialogEl: Ref<HTMLElement | null>
  formId: Ref<string | number | null>
  initialWidthPx: Ref<number>
  initialHeightPx: Ref<number | null>
  preferredWidthPx: Ref<number>
  preferredHeightPx: Ref<number | null>
}

const getViewport = () => ({
  width: typeof window !== 'undefined' ? window.innerWidth : 1920,
  height: typeof window !== 'undefined' ? window.innerHeight : 1080,
})

const setDocumentResizing = (active: boolean) => {
  if (typeof document === 'undefined') {
    return
  }
  document.documentElement.classList.toggle(PLATFORM_DIALOG_RESIZING_CLASS, active)
}

const sizesClose = (a: number, b: number): boolean => Math.abs(a - b) <= SIZE_EQ_PX

const resetDialogSurfacePosition = (dialogEl: HTMLElement | null) => {
  if (!dialogEl) {
    return
  }
  // PrimeVue drag sets position:fixed + margin:0; clearing only top/left pins the dialog to the top.
  dialogEl.style.position = ''
  dialogEl.style.top = ''
  dialogEl.style.left = ''
  dialogEl.style.margin = ''
}

export const usePlatformDialogResize = (options: UsePlatformDialogResizeOptions) => {
  const widthPx = ref(options.initialWidthPx.value)
  const heightPx = ref<number | null>(options.initialHeightPx.value)
  const userResized = ref(false)
  const maximized = ref(false)

  let activePointerId: number | null = null
  let resizeHandle: HTMLElement | null = null
  let startX = 0
  let startY = 0
  let startWidth = 0
  let startHeight = 0

  const syncFromInitial = () => {
    if (userResized.value) {
      return
    }
    widthPx.value = options.initialWidthPx.value
    const nextHeight = options.initialHeightPx.value
    // Пока form stub без tables → height null; не сбрасывать уже посчитанную высоту (flash→collapse).
    if (nextHeight != null || heightPx.value == null) {
      heightPx.value = nextHeight
    }
  }

  const persistSize = () => {
    const formId = options.formId.value
    if (formId == null || formId === '') {
      return
    }
    saveDialogSize(formId, widthPx.value, heightPx.value)
  }

  const releaseResize = () => {
    if (resizeHandle && activePointerId != null && resizeHandle.hasPointerCapture(activePointerId)) {
      resizeHandle.releasePointerCapture(activePointerId)
    }
    activePointerId = null
    resizeHandle = null
    setDocumentResizing(false)
    window.removeEventListener('pointermove', onPointerMove, true)
    window.removeEventListener('pointerup', stopResize, true)
    window.removeEventListener('pointercancel', stopResize, true)
  }

  const onPointerMove = (event: PointerEvent) => {
    if (activePointerId !== event.pointerId) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const viewport = getViewport()
    widthPx.value = clampDialogResizeWidth(
      startWidth + (event.clientX - startX),
      viewport,
    )

    const baseHeight = startHeight || options.dialogEl.value?.getBoundingClientRect().height || PLATFORM_DIALOG_MIN_HEIGHT
    heightPx.value = clampDialogResizeHeight(
      baseHeight + (event.clientY - startY),
      viewport,
    )
  }

  const stopResize = (event: PointerEvent) => {
    if (activePointerId !== event.pointerId) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    persistSize()
    releaseResize()
  }

  const onResizePointerDown = (event: PointerEvent) => {
    if (event.button !== 0) {
      return
    }

    const handle = event.currentTarget
    const dialogEl = options.dialogEl.value
    if (!(handle instanceof HTMLElement) || !dialogEl) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    userResized.value = true
    maximized.value = false
    resetDialogSurfacePosition(dialogEl)
    activePointerId = event.pointerId
    resizeHandle = handle
    startX = event.clientX
    startY = event.clientY
    startWidth = widthPx.value
    startHeight = heightPx.value ?? dialogEl.getBoundingClientRect().height

    setDocumentResizing(true)
    handle.setPointerCapture(event.pointerId)

    window.addEventListener('pointermove', onPointerMove, true)
    window.addEventListener('pointerup', stopResize, true)
    window.addEventListener('pointercancel', stopResize, true)
  }

  const applyExpandedBox = () => {
    resetDialogSurfacePosition(options.dialogEl.value)
  }

  const expandToMax = () => {
    const viewport = getViewport()
    userResized.value = true
    maximized.value = true
    widthPx.value = maxResizableDialogWidth(viewport)
    heightPx.value = maxResizableDialogHeight(viewport)
    persistSize()
    applyExpandedBox()
    void nextTick(applyExpandedBox)
  }

  const collapseToPreferred = () => {
    userResized.value = true
    maximized.value = false
    widthPx.value = options.preferredWidthPx.value
    heightPx.value = options.preferredHeightPx.value
    persistSize()
    applyExpandedBox()
    void nextTick(applyExpandedBox)
  }

  const isMaximized = computed(() => maximized.value)

  const isAtPreferred = computed(() => {
    if (!userResized.value) {
      return true
    }
    if (!sizesClose(widthPx.value, options.preferredWidthPx.value)) {
      return false
    }
    const preferredHeight = options.preferredHeightPx.value
    if (preferredHeight == null) {
      return heightPx.value == null
    }
    return heightPx.value != null && sizesClose(heightPx.value, preferredHeight)
  })

  watch([options.initialWidthPx, options.initialHeightPx], () => {
    syncFromInitial()
  })

  onBeforeUnmount(() => {
    releaseResize()
  })

  return {
    widthPx,
    heightPx,
    userResized,
    isMaximized,
    isAtPreferred,
    syncFromInitial,
    onResizePointerDown,
    expandToMax,
    collapseToPreferred,
  }
}
